// Las plantillas de "Recrear video" — ADR-2026-10-09-recrear-video-adentro-de-recrear y
// ADR-2026-10-16-recrear-video-con-nuestra-receta.
//
// Nueve, armadas por Atlas y probadas con la receta del backend (las seis últimas, de Genjutsu de
// Higgsfield: ADR-2026-11-02-recrear-seis-plantillas-de-genjutsu): cada una trae el video de
// referencia y le pide al usuario lo que falta ("Tu producto", "La persona"). Los `id` son
// los de `PLANTILLAS_RECREAR` del backend (atlas-backend `services/recrear/plantillas.ts`):
// al generar viaja sólo el id y el backend usa su video, ya analizado.
//
// Duran 10 s: 30 créditos. Cambiar un video es cambiarlo en los dos lados (acá para la vista,
// en el backend con otra `version`).

import type { RolDeImagen } from './recrearVideo';

const SISTEMA = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system';

export interface PlantillaDeRecrearVideo {
  id: string;
  titulo: string;
  bajada: string;
  /** Sólo para mostrarla: al generar viaja el `id`. */
  videoUrl: string;
  /** Lo que se usa del video (y lo que se cobra): 10 s. */
  duracionSeg: number;
  /** Las imágenes que pide, en orden. */
  pide: { rol: RolDeImagen; texto: string }[];
  /** Lo que se precarga en las indicaciones. El usuario lo puede cambiar o borrar. Vacío = no se precarga nada. */
  indicaciones: string;
  /**
   * El ejemplo gris del cuadro de indicaciones, cuando la plantilla sirve para cosas distintas. La de la modelo puede
   * cambiar sólo la ropa o también a la persona: precargar "que la modelo sea la de mi foto" empujaba siempre a lo
   * segundo, y sin texto el backend decide por las fotos y su rol (prenda → ropa; persona → persona).
   */
  ejemplo?: string;
}

export const PLANTILLAS_DE_RECREAR_VIDEO: readonly PlantillaDeRecrearVideo[] = [
  {
    id: 'producto-en-primer-plano',
    titulo: 'Producto en primer plano',
    bajada: 'La misma toma de producto, con el tuyo.',
    videoUrl: `${SISTEMA}/video_tipos/producto-heroe.mp4`,
    duracionSeg: 10,
    pide: [{ rol: 'producto', texto: 'Tu producto' }],
    indicaciones: 'Poné mi producto en lugar de la gorra.',
  },
  {
    id: 'modelo-bajo-los-arcos',
    titulo: 'Modelo bajo los arcos',
    bajada: 'Una modelo camina por la ciudad con tu ropa.',
    // v2 (ADR-2026-10-19-recrear-arcos-sin-anteojos): la misma toma con una modelo sin anteojos de sol.
    videoUrl: `${SISTEMA}/recrear/modelo-bajo-los-arcos-v2.mp4`,
    duracionSeg: 10,
    pide: [
      { rol: 'prenda', texto: 'Tu ropa' },
      { rol: 'persona', texto: 'La persona (opcional)' },
    ],
    indicaciones: '',
    ejemplo: 'Opcional. Ej.: que lleve mi campera, o que la modelo sea la de mi foto',
  },
  {
    id: 'producto-flotando',
    titulo: 'Producto flotando',
    bajada: 'Tu producto gira y flota sobre un fondo de color.',
    videoUrl: `${SISTEMA}/recrear/producto-flotando.mp4`,
    duracionSeg: 10,
    pide: [{ rol: 'producto', texto: 'Tu producto' }],
    indicaciones: 'Cambiá el sobre del video por mi producto.',
  },
  {
    id: 'modelo-en-el-pasillo',
    titulo: 'Modelo en el pasillo',
    bajada: 'Una modelo camina hacia la cámara con tu ropa.',
    videoUrl: `${SISTEMA}/recrear/modelo-en-el-pasillo.mp4`,
    duracionSeg: 10,
    pide: [
      { rol: 'prenda', texto: 'Tu ropa' },
      { rol: 'persona', texto: 'La persona (opcional)' },
    ],
    indicaciones: '',
    ejemplo: 'Opcional. Ej.: que lleve mi musculosa y mi pantalón, o que la modelo sea la de mi foto',
  },
  {
    id: 'camisa-en-detalle',
    titulo: 'Camisa en detalle',
    bajada: 'Se acomoda los puños de tu camisa, sin mostrar la cara.',
    videoUrl: `${SISTEMA}/recrear/camisa-en-detalle.mp4`,
    duracionSeg: 10,
    pide: [{ rol: 'prenda', texto: 'Tu prenda' }],
    indicaciones: 'Que la camisa del video sea la mía.',
  },
  {
    id: 'paseo-por-la-calle',
    titulo: 'Paseo por la calle',
    bajada: 'Un chico camina por la calle con tu ropa.',
    videoUrl: `${SISTEMA}/recrear/paseo-por-la-calle.mp4`,
    duracionSeg: 10,
    pide: [
      { rol: 'prenda', texto: 'Tu ropa' },
      { rol: 'persona', texto: 'La persona (opcional)' },
    ],
    indicaciones: '',
    ejemplo: 'Opcional. Ej.: que lleve mi camisa, o que el modelo sea el de mi foto',
  },
  {
    id: 'giro-en-estudio',
    titulo: 'Giro en estudio',
    bajada: 'Una modelo posa y gira en un estudio blanco con tu ropa.',
    videoUrl: `${SISTEMA}/recrear/giro-en-estudio.mp4`,
    duracionSeg: 10,
    pide: [
      { rol: 'prenda', texto: 'Tu ropa' },
      { rol: 'persona', texto: 'La persona (opcional)' },
    ],
    indicaciones: '',
    ejemplo: 'Opcional. Ej.: que lleve mi sweater y mi jean todo el video',
  },
  {
    id: 'zapatillas-en-la-mano',
    titulo: 'Zapatillas en la mano',
    bajada: 'Una mano muestra tu calzado de cerca.',
    videoUrl: `${SISTEMA}/recrear/zapatillas-en-la-mano.mp4`,
    duracionSeg: 10,
    pide: [{ rol: 'producto', texto: 'Tu calzado' }],
    indicaciones: 'Poné mis zapatillas en lugar de las del video.',
  },
  {
    id: 'bebida-con-salpicadura',
    titulo: 'Bebida con salpicadura',
    bajada: 'Tu bebida entre hielo y salpicaduras.',
    videoUrl: `${SISTEMA}/recrear/bebida-con-salpicadura.mp4`,
    duracionSeg: 10,
    pide: [{ rol: 'producto', texto: 'Tu producto' }],
    indicaciones: 'Cambiá la lata del video por mi producto.',
  },
];

export const plantillaPorId = (id: string | null | undefined): PlantillaDeRecrearVideo | null =>
  PLANTILLAS_DE_RECREAR_VIDEO.find((p) => p.id === id) ?? null;
